import { CaptchaComp } from '@flowus/login/uikit/web/captcha';
import { md5Sum, Regex } from '@flowus/login/utils';
import { CaptchaSendType } from '@next-space/fe-api-idl';
import type { FC, FormEvent, MouseEvent } from 'react';
import { useState } from 'react';
import { Button } from 'src/common/components/button';
import { Icon } from 'src/common/components/icon';
import { Input } from 'src/common/components/input';
import { request } from 'src/common/request';
import { useCurrentUser } from 'src/hooks/user';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

export enum SettingPasswordType {
  BY_ACCOUNT = 0,
  BY_OLD_PASSWORD = 1,
}
interface Props {
  type: SettingPasswordType;
  phone?: string;
  onSuccess: () => void;
}

export const SettingPassword: FC<Props> = (props) => {
  const { t } = useAppTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const {
    password,
    passwordError,
    newPassword,
    newPasswordError,
    verifyCode,
    setVerifyCode,
    verifyCodeError,
    setPassword,
    setNewPassword,
    settingPassword,
  } = useSettingPassword(props.type, props.phone);

  const onSubmit = async (e: MouseEvent | FormEvent) => {
    e.preventDefault();
    const success = await settingPassword();
    if (success) props.onSuccess();
  };

  if (props.type === SettingPasswordType.BY_ACCOUNT && props.phone) {
    return (
      <div className="next-modal p-7">
        <div className="text-t1-medium ">{t('common.set_password')}</div>
        <Input
          inputType={showPassword ? 'text' : 'password'}
          warning={passwordError}
          className="w-full h-9 mt-5"
          inputClassName="opacity-50"
          value={password}
          placeholder={t('components.login_uikit.enter_new_password')}
          onChange={setPassword}
          addonAfter={
            <Icon
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowPassword(!showPassword);
              }}
              name={showPassword ? 'IcHide' : 'IcShow'}
              className="cursor-pointer absolute right-2.5 text-grey3"
              size="middle"
            />
          }
        />
        <div className="text-t2 text-grey3 mt-2.5">
          {t('components.login_uikit.password_requirement')}
        </div>
        <Input
          readonly={true}
          className="w-full h-9 mt-5"
          inputClassName="opacity-50"
          value={props.phone}
        />
        <Input
          autoFocus
          type="number"
          value={verifyCode}
          maxLength={6}
          warning={verifyCodeError}
          className="w-full h-9 mt-5 px-3"
          inputClassName="opacity-50 px-0"
          placeholder={t('common.enter_verification_code')}
          onChange={(v) => setVerifyCode(v.toString())}
          addonAfter={
            <CaptchaComp
              onSendVerifyCode={() => {
                if (__BUILD_IN__) {
                  return request.infra.emailCaptcha({
                    email: props.phone ?? '',
                    type: CaptchaSendType.RESET_PASSWORD,
                  });
                }
                return request.infra.createPhoneCaptcha(props.phone ?? '', {
                  type: CaptchaSendType.RESET_PASSWORD,
                });
              }}
            />
          }
        />
        <Button
          onClick={async () => {
            const success = await settingPassword();
            if (success) props.onSuccess();
          }}
          colorType="active"
          className="mt-6"
        >
          {t('components.login_uikit.complete_binding')}
        </Button>
      </div>
    );
  }

  // 已经设置过的，需要用老密码新密码重新设置
  return (
    <form className="px-7 pb-7" onSubmit={onSubmit}>
      <div className="text-t4 text-grey3 mt-9">{t('components.login_uikit.old_password')}</div>
      <Input
        inputType={showPassword ? 'text' : 'password'}
        warning={passwordError}
        className="w-full h-9 mt-5"
        inputClassName="opacity-50"
        value={password}
        placeholder={t('components.login_uikit.enter_old_password')}
        onChange={setPassword}
        addonAfter={
          <Icon
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowPassword(!showPassword);
            }}
            name={showPassword ? 'IcHide' : 'IcShow'}
            className="cursor-pointer absolute right-2.5 text-grey3"
            size="middle"
          />
        }
      />
      <div className="text-t4 text-grey3 mt-6">{t('components.login_uikit.new_password')}</div>
      <Input
        inputType={showNewPassword ? 'text' : 'password'}
        warning={newPasswordError}
        className="w-full h-9 mt-5"
        inputClassName="opacity-50"
        value={newPassword}
        placeholder={t('components.login_uikit.enter_new_password')}
        onChange={setNewPassword}
        addonAfter={
          <Icon
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowNewPassword(!showNewPassword);
            }}
            name={showNewPassword ? 'IcHide' : 'IcShow'}
            className="cursor-pointer absolute right-2.5 text-grey3"
            size="middle"
          />
        }
      />
      <div className="text-t2 text-grey3 mt-2.5">
        {t('components.login_uikit.password_requirement')}
      </div>
      <Button onClick={onSubmit} type="submit" colorType="active" className="mt-6">
        {t('common.set_password')}
      </Button>
    </form>
  );
};

const useSettingPassword = (type: SettingPasswordType, _account?: string) => {
  const { t } = useAppTranslation();
  const [account, _] = useState<string>(_account ?? '');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyCodeError, setVerifyCodeError] = useState('');
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const currentUser = useCurrentUser();

  const checkVerifyCode = () => {
    if (!Regex.VerifyCode.test(verifyCode)) {
      setVerifyCodeError(t('common.verification_code_error'));
      return false;
    }
    return true;
  };
  const checkPassword = () => {
    if (!password || !Regex.Password.test(password)) {
      setPasswordError(t('components.login_uikit.password_format_error'));
      return false;
    }
    return true;
  };
  const checkNewPassword = () => {
    if (!newPassword || !Regex.Password.test(newPassword)) {
      setNewPasswordError(t('components.login_uikit.password_format_error'));
      return false;
    }
    return true;
  };
  /** 设置密码 */
  const settingPassword = async () => {
    if (!checkPassword()) return;
    if (type === SettingPasswordType.BY_ACCOUNT) {
      if (!checkVerifyCode()) return;
      if (__BUILD_IN__) {
        return request.infra.resetUserPasswordByEmail({
          email: account,
          password: md5Sum(password),
          captcha: verifyCode,
        });
      }

      return request.infra.resetUserPasswordByPhone(account, {
        captcha: verifyCode,
        password: md5Sum(password),
      });
    } else if (checkNewPassword()) {
      const res = await request.infra.resetUserPassword(currentUser.uuid, {
        old: md5Sum(password),
        new: md5Sum(newPassword),
      });
      return res;
    }
  };
  return {
    phone: account,
    password,
    passwordError,
    newPassword,
    newPasswordError,
    verifyCode,
    verifyCodeError,
    setPassword,
    setNewPassword,
    settingPassword,
    setVerifyCode,
  };
};
